import { profileApi } from '../profileApi';
import { ProfileView } from '../types/profileTypes';
import {
  CodeNameView,
  VerifierAssignmentConfigView,
} from '../types/vobAssignmentTypes';
import {
  VobAssignmentsBulkAddRequest,
  VobAssignmentsUpdateRequest,
  VobAssignmentRemoveRequest,
} from '../requests/vobAssignmentRequests';

/**
 * VOB assignment endpoints — the VobManager-gated surface for assigning verifiers to payer
 * groups and centers. Mutations invalidate the verifier assignment config so the management
 * grid re-reads after every change. All endpoints require the `VobManager` claim server-side
 * (403 otherwise).
 */
const vobAssignmentEndpoints = profileApi.injectEndpoints({
  endpoints: (builder) => ({
    // Dimensional lookups for the assignment form dropdowns.
    // GET /AllProcessTypes
    getProcessTypes: builder.query<CodeNameView[], void>({
      query: () => 'AllProcessTypes',
    }),

    // GET /AllAssignmentTypes
    getAssignmentTypes: builder.query<CodeNameView[], void>({
      query: () => 'AllAssignmentTypes',
    }),

    // Per-process verifier assignment configuration (the management grid's data source).
    // GET /Profile/VobAssignments/Config?Process=
    getVerifierAssignments: builder.query<VerifierAssignmentConfigView[], string>({
      query: (process) => `Profile/VobAssignments/Config?Process=${encodeURIComponent(process)}`,
      providesTags: ['vobAssignments'],
    }),

    // Add assignment values across (value, type, process) rows for a verifier.
    // POST /Profile/VobAssignment/BulkAdd
    bulkAddVobAssignments: builder.mutation<ProfileView, VobAssignmentsBulkAddRequest>({
      query: (body) => ({
        url: 'Profile/VobAssignment/BulkAdd',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['vobAssignments', 'userProfile'],
    }),

    // Atomically replace a (type, process) entry's values for a verifier.
    // PUT /Profile/VobAssignment/Update
    updateVobAssignments: builder.mutation<ProfileView, VobAssignmentsUpdateRequest>({
      query: (body) => ({
        url: 'Profile/VobAssignment/Update',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['vobAssignments', 'userProfile'],
    }),

    // Remove a value, or the whole (type, process) entry, from a verifier.
    // DELETE /Profile/VobAssignment/Remove
    removeVobAssignments: builder.mutation<ProfileView, VobAssignmentRemoveRequest>({
      query: (body) => ({
        url: 'Profile/VobAssignment/Remove',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['vobAssignments', 'userProfile'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProcessTypesQuery,
  useGetAssignmentTypesQuery,
  useGetVerifierAssignmentsQuery,
  useBulkAddVobAssignmentsMutation,
  useUpdateVobAssignmentsMutation,
  useRemoveVobAssignmentsMutation,
} = vobAssignmentEndpoints;
